// Shared by the ordinary results and the peer-gap "featured" cards, which render through the same
// Product component and so need exactly the same fields. Keeping one copy stops the two drifting
// apart and a featured card silently losing, say, its multi-buy prices.
const PRODUCT_FIELDS = `
            uuid
            itemId
            itemName
            averageScore
            category1Id
            category1Name
            category2Id
            category2Name
            category3Id
            category3Name
            categoryName
            isNew
            brand
            previouslyPurchased
            reviewCount
            tags
            quantityStep
            quantityMax
            delivery {
                price
                priceInc
                step
                max
                unitPriceDisplay
            }
            collection {
                price
                priceInc
                step
                max
                unitPriceDisplay
            }
            collectionMultiBuy {
                price
                priceInc
                step
                min
                max
                unitPriceDisplay
            }
            deliveryMultiBuy {
                price
                priceInc
                step
                min
                max
                unitPriceDisplay
            }
            productDescription
            offer {
                onOffer
                promoTypes
                promoTag
                promoTagId
                promoEnd
                promoDiscountText
                promoForCc
                promoDisAmt
                promoMinQty
                promoPrice
            }
            favourite
            imageLinks
            isAvailable
            price
            productFeatures
            origin`;

const FEATURED_SELECTION = `
        featured {
            position
            reason
            campaignId
            title
            intro
            product {
${PRODUCT_FIELDS}
            }
        }`;

/**
 * The search query.
 *
 * DEPLOY ORDER IS LOAD-BEARING because of the `featured` selection. graphql-gateway composes its
 * supergraph once at pod start and never re-polls (IntrospectAndCompose is constructed without
 * pollIntervalInMs, and the gateway-level pollIntervalInMs only applies to serviceList configs).
 * Asking it for a field it has not composed fails the *whole* operation — data: null — so search
 * returns nothing at all for every customer, not just the trial accounts.
 *
 * So: deploy graph-product-search, restart the gateway, then deploy olympus. And a rollback of
 * graph-product-search past the `featured` field requires rolling olympus back with it. The same
 * holds for every field inside it: `featured.title` and `featured.intro` need a graph-product-search that serves them,
 * and a gateway restarted after that deploy, before this ships. The same applies to
 * `correctedQuery` and the `correct` argument.
 *
 * Selecting `correctedQuery` is what opts this client in to spelling correction: a search that
 * finds nothing comes back with the results for the corrected words and `correctedQuery` set.
 */
export const GQL_GET_SEARCH = `
    query(
        $page: Int
        $size: Int
        $branch: String
        $deliveryDate: Date
        $brand: String
        $productFeatures: [String!]
        $sizeOrCut: [String!]
        $isNew: Boolean
        $sortType: String
        $origin: String
        $query: String!
        $categoryIds: [String!]
        $categoryNames: [String!]
        $listId: String
        $brandOwner: String
        $includeNonPublicItems: Boolean
        $first: [String!]
        $advanced: Boolean
        $dynamicOverlay: Boolean
        $correct: Boolean
    ) {
        searchProducts (
            page: $page
            size: $size
            branch: $branch
            deliveryDate: $deliveryDate
            brand: $brand
            productFeatures: $productFeatures
            sizeOrCut: $sizeOrCut
            isNew: $isNew
            sortType: $sortType
            origin: $origin
            query: $query
            categoryIds: $categoryIds
            categoryNames: $categoryNames
            listId: $listId
            brandOwner: $brandOwner
            includeNonPublicItems: $includeNonPublicItems
            first: $first
            advanced: $advanced
            dynamicOverlay: $dynamicOverlay
            correct: $correct
        ) {
        searchId
        correctedQuery
        page {
            number
            size
            totalPages
            totalElements
        }
        results {
${PRODUCT_FIELDS}
        }
${FEATURED_SELECTION}
        facets {
            name
            data {
            id
            name
            qty
            unquery
            query
            selected
            counts {
                id
                name
                qty
                unquery
                query
                selected
                counts {
                    id
                    name
                    qty
                    unquery
                    query
                    selected
                }
            }
            }
        }
        }
    }
`;

/**
 * Products to show under a search that found little or nothing. graph-product-search decides
 * whether there is anything to show (strategy NONE otherwise) and writes the heading; see the
 * `relatedProducts` field in its schema. Same deploy-order rule as GQL_GET_SEARCH.
 */
export const GQL_RELATED_PRODUCTS = `
    query(
        $branch: String
        $deliveryDate: Date
        $query: String
        $itemIds: [String!]
        $excludeItemIds: [String!]
        $resultCount: Int!
        $parentSearchId: String
    ) {
        relatedProducts (
            branch: $branch
            deliveryDate: $deliveryDate
            query: $query
            itemIds: $itemIds
            excludeItemIds: $excludeItemIds
            resultCount: $resultCount
            parentSearchId: $parentSearchId
        ) {
        searchId
        strategy
        title
        searchedFor
        results {
${PRODUCT_FIELDS}
        }
        }
    }
`;
